import type { ReactNode } from 'react';
import { Link as RouterLink, useLocation } from 'wouter';
import { KeyRound, LayoutGrid, LogOut, Presentation, Users } from 'lucide-react';

import { logout, useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const NAV_ITEMS = [
  { href: '/', label: 'Liste', icon: LayoutGrid, match: (path: string) => path === '/' },
  {
    href: undefined,
    label: 'Sim',
    icon: Presentation,
    match: (path: string) => path.startsWith('/s/'),
  },
  { href: '/teams', label: 'Équ.', icon: Users, match: (path: string) => path === '/teams' },
] as const;

function initials(label: string): string {
  return label
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

/**
 * Rail de navigation persistant (68px) commun aux écrans authentifiés
 * (Simulation, Liste des simulations, Équipe) — remplace les en-têtes ad-hoc
 * de chaque page. Absent des pages publiques (connexion, partage public).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [path] = useLocation();
  const { user } = useAuth();
  const label = String(user?.name || user?.email || '');

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <nav className="flex w-[68px] shrink-0 flex-col items-center gap-5 border-r bg-card py-5 print:hidden">
        <RouterLink
          href="/"
          className="flex size-8 items-center justify-center rounded-[10px] bg-primary text-sm font-bold text-primary-foreground"
        >
          S
        </RouterLink>

        <div className="flex flex-col gap-2">
          {NAV_ITEMS.map(({ href, label: navLabel, icon: Icon, match }) => {
            const active = match(path);
            const content = (
              <span
                className={cn(
                  'flex size-[38px] items-center justify-center rounded-[11px] text-[11px] font-semibold',
                  active
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="size-4" />
              </span>
            );
            return (
              <Tooltip key={navLabel}>
                <TooltipTrigger asChild>
                  {href ? (
                    <RouterLink href={href} aria-label={navLabel}>
                      {content}
                    </RouterLink>
                  ) : (
                    <span aria-hidden className="cursor-default">
                      {content}
                    </span>
                  )}
                </TooltipTrigger>
                <TooltipContent side="right">{navLabel}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        <div className="mt-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Compte"
                className="flex size-8 items-center justify-center rounded-full border text-[11px] font-semibold"
              >
                {initials(label) || '?'}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="right">
              {label && (
                <>
                  <DropdownMenuLabel className="truncate font-normal text-muted-foreground">
                    {label}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem asChild>
                <RouterLink href="/api-keys">
                  <KeyRound />
                  Clés API
                </RouterLink>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => logout()}>
                <LogOut />
                Se déconnecter
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
